import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/apiClient';
import API_CONFIG from '../../config/apiConfig';
import { getCoursePassportProgressions } from '../../config/careerPassports';
import './CourseRecommendations.css';

const getStoredAuthHeader = () => {
  const accessToken = localStorage.getItem('access_token');
  if (accessToken) return `Bearer ${accessToken}`;

  const legacyToken = localStorage.getItem('token');
  return legacyToken ? `Token ${legacyToken}` : null;
};

const CourseRecommendations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [loadingEnrollmentId, setLoadingEnrollmentId] = useState(null);

  useEffect(() => {
    loadRecommendations();
  }, []);

  // Track recommendation changes (no console spam)
  useEffect(() => {}, [recommendations]);

  const loadRecommendations = async () => {
    try {
      // Support both token and session-based auth; no hard block on missing token
      const response = await api.get('/career/recommendations/');
      const data = response.data;


        // Handle paginated response
        let recommendationsArray = [];

        if (data && typeof data === 'object' && data.results && Array.isArray(data.results)) {
          // Paginated response - default format
          recommendationsArray = data.results;
        } else if (Array.isArray(data)) {
          // Direct array response
          recommendationsArray = data;
        } else {
          recommendationsArray = [];
        }

        setRecommendations(recommendationsArray);

    } catch (error) {
      console.warn('⚠️ API client failed to load recommendations, attempting direct fallback...', error?.message || error);
      // Fallback: call backend directly in case the dev proxy isn't working
      try {
        const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const authHeader = getStoredAuthHeader();
        const res = await fetch(`${backendBase}/api/career/recommendations/`, {
          headers: {
            'Content-Type': 'application/json',
            ...(authHeader ? { 'Authorization': authHeader } : {}),
          },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          let arr = [];
          if (data && typeof data === 'object' && Array.isArray(data.results)) arr = data.results;
          else if (Array.isArray(data)) arr = data;
          setRecommendations(arr);
        } else {
          console.error('❌ Fallback fetch failed:', res.status, res.statusText);
          setRecommendations([]);
        }
      } catch (e) {
        console.error('❌ Fallback request error:', e);
        setRecommendations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const regenerateRecommendations = async () => {
    setLoading(true);
    try {
      // Session or token-based auth supported; API will enforce auth status
      const { data: result } = await api.post('/career/recommendations/regenerate/');
      // Reload recommendations
      await loadRecommendations();
      alert(`🎉 Successfully regenerated ${result.recommendations_generated || 0} new recommendations!`);
    } catch (error) {
      console.warn('⚠️ API client failed to regenerate; attempting direct fallback...', error?.message || error);
      try {
        const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const authHeader = getStoredAuthHeader();
        const res = await fetch(`${backendBase}/api/career/recommendations/regenerate/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authHeader ? { 'Authorization': authHeader } : {}),
            'X-CSRFToken': (document.cookie.match(/csrftoken=([^;]+)/) || [])[1] || ''
          },
          credentials: 'include',
        });
        if (res.ok) {
          const result = await res.json();
          await loadRecommendations();
          alert(`🎉 Successfully regenerated ${result.recommendations_generated || 0} new recommendations!`);
        } else {
          console.error('❌ Fallback regenerate failed:', res.status, res.statusText);
          alert('Error regenerating recommendations. Please try again.');
        }
      } catch (e) {
        console.error('❌ Fallback regenerate error:', e);
        alert('Error regenerating recommendations. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCourses(prev => 
      prev.includes(courseId) 
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handleAcceptLearning = async (recommendation) => {
    if (!recommendation?.is_lms_hosted) return;

    try {
      setLoadingEnrollmentId(recommendation.id);
      const response = await api.post(API_CONFIG.lms.acceptEnrollment(), {
        recommendation_id: recommendation.id,
      });
      const enrollment = response.data || {};
      const pendingPath = enrollment.pending_url || `/learning/${enrollment.public_id}`;
      navigate(pendingPath, {
        state: {
          enrollment,
          recommendation,
        },
      });
    } catch (error) {
      console.error('Failed to accept LMS recommendation:', error);
      alert('We could not start the LMS handoff just yet. Please try again.');
    } finally {
      setLoadingEnrollmentId(null);
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return '#28a745';
    if (score >= 80) return '#ffc107';
    if (score >= 70) return '#fd7e14';
    return '#dc3545';
  };

  const getMatchScoreLabel = (score) => {
    if (score >= 90) return 'Excellent Match';
    if (score >= 80) return 'Very Good Match';
    if (score >= 70) return 'Good Match';
    return 'Fair Match';
  };

  if (loading) {
    return (
      <div className="recommendations-loading">
        <div className="loading-spinner"></div>
        <p>Loading your personalized recommendations...</p>
      </div>
    );
  }



  return (
    <div className="course-recommendations">
      <div className="recommendations-header">
        <h1>🎯 Your Course Recommendations</h1>
        <p>Based on your skills, interests, and career goals, here are the training programs we recommend for you.</p>
        
        {location.state?.recommendationsGenerated && (
          <div className="success-message">
            ✅ Assessment complete! We've generated {location.state.recommendationsGenerated} personalized recommendations for you.
          </div>
        )}

        <div className="header-actions">
          <Link
            to="/career"
            state={{ assessmentCompleted: true }}
            className="btn-secondary"
          >
            ← Back to Dashboard
          </Link>

          <button className="btn-secondary" onClick={regenerateRecommendations}>
            🔄 Regenerate Recommendations
          </button>

          {selectedCourses.length >= 2 && (
            <Link
              to="/career/compare"
              state={{ courseIds: selectedCourses }}
              className="btn-primary"
            >
              📊 Compare Selected ({selectedCourses.length})
            </Link>
          )}
        </div>
      </div>

      {!Array.isArray(recommendations) || recommendations.length === 0 ? (
        <div className="no-recommendations">
          <div className="no-recommendations-content">
            <h2>🤔 No Recommendations Yet</h2>
            <p>We need more information about your skills and interests to provide personalized recommendations.</p>
            <Link to="/career/assessment" className="btn-primary">
              Take Career Assessment
            </Link>
          </div>
        </div>
      ) : (
        <div className="recommendations-grid">
          {Array.isArray(recommendations) && recommendations.map((recommendation) => (
            <RecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
              isSelected={selectedCourses.includes(recommendation.course)}
              onToggleSelection={() => toggleCourseSelection(recommendation.course)}
              onAcceptLearning={() => handleAcceptLearning(recommendation)}
              isAccepting={loadingEnrollmentId === recommendation.id}
              getMatchScoreColor={getMatchScoreColor}
              getMatchScoreLabel={getMatchScoreLabel}
            />
          ))}
        </div>
      )}

      {Array.isArray(recommendations) && recommendations.length > 0 && (
        <div className="recommendations-footer">
          <div className="footer-info">
            <h3>💡 How We Calculate Matches</h3>
            <div className="match-explanation">
              <div className="match-factor">
                <span className="factor-icon">🎓</span>
                <div>
                  <strong>Academic Match</strong>
                  <p>Based on your education level and qualifications</p>
                </div>
              </div>
              <div className="match-factor">
                <span className="factor-icon">🛠️</span>
                <div>
                  <strong>Skills Match</strong>
                  <p>How well your current skills align with course content</p>
                </div>
              </div>
              <div className="match-factor">
                <span className="factor-icon">❤️</span>
                <div>
                  <strong>Interest Match</strong>
                  <p>Based on your career field preferences</p>
                </div>
              </div>
              <div className="match-factor">
                <span className="factor-icon">⚙️</span>
                <div>
                  <strong>Practical Match</strong>
                  <p>Considers location, time, and study format preferences</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RecommendationCard = ({ 
  recommendation, 
  isSelected, 
  onToggleSelection, 
  onAcceptLearning,
  isAccepting,
  getMatchScoreColor, 
  getMatchScoreLabel 
}) => {
  const [expanded, setExpanded] = useState(false);
  const passportLinks = getCoursePassportProgressions({
    programme_name: recommendation.course_name,
    passport_links: recommendation.passport_links,
    passport_alignment_reviewed: recommendation.passport_alignment_reviewed
  });

  return (
    <div className={`recommendation-card ${isSelected ? 'selected' : ''}`}>
      <div className="recommendation-card-header">
        <div className="recommendation-course-info">
          <h3>{recommendation.course_name}</h3>
          <div className="recommendation-passport-row" aria-label="Passport pathway">
            <span className="recommendation-passport-label">Passport pathway</span>
            {passportLinks.length > 0 ? passportLinks.map(passport => (
              <span
                key={passport.id}
                className={`recommendation-passport-badge recommendation-passport-badge--${passport.tone}`}
                title={passport.coverage}
              >
                <span className="recommendation-passport-dot" aria-hidden="true" />
                {passport.title}
              </span>
            )) : (
              <span className="recommendation-passport-badge recommendation-passport-badge--pending">
                {recommendation.passport_alignment_reviewed ? 'No Passport pathway linked' : 'Passport mapping needed'}
              </span>
            )}
          </div>
          <div className="course-meta">
            <span className="course-type">{recommendation.course_type?.replace('_', ' ')}</span>
            <span className="course-duration">{recommendation.course_duration} months</span>
            <span className="campus-name">{recommendation.campus_name}</span>
            {recommendation.is_lms_hosted && (
              <span className="delivery-mode-pill lms-pill">LMS</span>
            )}
          </div>
        </div>
        
        <div className="match-score">
          <div 
            className="score-circle"
            style={{ borderColor: getMatchScoreColor(recommendation.overall_match_score) }}
          >
            <span className="score-number">{Math.round(recommendation.overall_match_score)}%</span>
          </div>
          <span 
            className="score-label"
            style={{ color: getMatchScoreColor(recommendation.overall_match_score) }}
          >
            {getMatchScoreLabel(recommendation.overall_match_score)}
          </span>
        </div>
      </div>

      <div className="recommendation-passport-info">
        <div className="recommendation-passport-info-title">What this Passport covers</div>
        {passportLinks.length > 0 ? passportLinks.map(passport => (
          <div className="recommendation-passport-detail" key={passport.id}>
            <strong>
              {passport.title}
              {passport.progressionLabel && <span> · {passport.progressionLabel}</span>}
            </strong>
            <span>{passport.coverage}</span>
            {!passport.progressionLabel && <span className="recommendation-passport-target">{passport.targetLabel}</span>}
            <small>{passport.nqf_levels || passport.nqfLevels} · {passport.progressionNote}</small>
          </div>
        )) : (
          <p className="recommendation-passport-pending-copy">
            {recommendation.passport_alignment_reviewed
              ? 'This course was reviewed and is not currently linked to a Passport pathway.'
              : 'This course still needs a confirmed Passport mapping.'}
          </p>
        )}
      </div>

      <div className="detailed-scores">
        <div className="score-breakdown">
          <div className="score-item">
            <span className="score-name">Academic</span>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ 
                  width: `${recommendation.academic_match_score}%`,
                  backgroundColor: getMatchScoreColor(recommendation.academic_match_score)
                }}
              ></div>
            </div>
            <span className="score-value">{Math.round(recommendation.academic_match_score)}%</span>
          </div>
          
          <div className="score-item">
            <span className="score-name">Skills</span>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ 
                  width: `${recommendation.skills_match_score}%`,
                  backgroundColor: getMatchScoreColor(recommendation.skills_match_score)
                }}
              ></div>
            </div>
            <span className="score-value">{Math.round(recommendation.skills_match_score)}%</span>
          </div>
          
          <div className="score-item">
            <span className="score-name">Interest</span>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ 
                  width: `${recommendation.interest_match_score}%`,
                  backgroundColor: getMatchScoreColor(recommendation.interest_match_score)
                }}
              ></div>
            </div>
            <span className="score-value">{Math.round(recommendation.interest_match_score)}%</span>
          </div>
          
          <div className="score-item">
            <span className="score-name">Practical</span>
            <div className="score-bar">
              <div 
                className="score-fill" 
                style={{ 
                  width: `${recommendation.practical_match_score}%`,
                  backgroundColor: getMatchScoreColor(recommendation.practical_match_score)
                }}
              ></div>
            </div>
            <span className="score-value">{Math.round(recommendation.practical_match_score)}%</span>
          </div>
        </div>
      </div>

      {recommendation.recommendation_reasons?.length > 0 && (
        <div className="recommendation-reasons">
          <h4>✅ Why This Course is Recommended:</h4>
          <ul>
            {recommendation.recommendation_reasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {recommendation.potential_challenges?.length > 0 && (
        <div className="potential-challenges">
          <h4>⚠️ Things to Consider:</h4>
          <ul>
            {recommendation.potential_challenges.map((challenge, index) => (
              <li key={index}>{challenge}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="card-actions">
        {recommendation.is_lms_hosted ? (
          <button
            type="button"
            onClick={onAcceptLearning}
            disabled={isAccepting}
            className="btn-primary"
          >
            {isAccepting ? 'Starting LMS handoff...' : 'Accept & Learn'}
          </button>
        ) : recommendation.external_provider_url ? (
          <a
            href={recommendation.external_provider_url}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
          >
            Visit Provider
          </a>
        ) : (
          <Link
            to={`/courses/${recommendation.course}`}
            className="btn-primary"
          >
            View Course Details
          </Link>
        )}
        
        <button 
          className={`btn-outline ${isSelected ? 'selected' : ''}`}
          onClick={onToggleSelection}
        >
          {isSelected ? 'Selected' : '+ Select for Comparison'}
        </button>
      </div>
    </div>
  );
};

export default CourseRecommendations;
